import React, { useState, useCallback, useEffect } from 'react';
import { AccountFast } from '../core/_models_fast';
import { APIError, useAxios } from '../core/_axios';
import { getMyAvailableAccounts } from '../core/_requests_v2';
import { setCurrentAccountUniqueIdentifierInLocalStorage } from '../core/_axios';

interface MobileAccountSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
}

const MobileAccountSwitcher: React.FC<MobileAccountSwitcherProps> = ({ isOpen, onClose }) => {
  const axios = useAxios();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<AccountFast[]>([]);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    if (!isOpen) return;
    
    setLoading(true);
    setError(null);
    try {
      const availableAccounts = await getMyAvailableAccounts(axios);
      setAccounts(availableAccounts);
    } catch (exception) {
      console.error(exception);
      const errorMessage = exception instanceof APIError ? exception.message : 'Failed to load accounts';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [axios, isOpen]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleAccountSelect = (account: AccountFast | null) => {
    if (account) {
      setCurrentAccountUniqueIdentifierInLocalStorage(account.unique_identifier);
    } else {
      // "All Accounts" selected: clear the working account under the key the rest of the app
      // reads (this used to remove a key nothing else used, so the tap did nothing).
      setCurrentAccountUniqueIdentifierInLocalStorage('');
    }
    window.location.reload();
  };

  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 transform -translate-y-1/2 bg-white dark:bg-coal-600 rounded-lg shadow-xl z-50 max-h-[80vh] flex flex-col lg:hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-coal-400">
          <h2 className="text-lg font-semibold">Switch Account</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-coal-500 rounded"
          >
            <i className="ki-filled ki-cross text-xl"></i>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b dark:border-coal-400">
          <div className="relative">
            <i className="ki-filled ki-magnifier absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              className="input input-sm pl-10 w-full"
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className="p-4 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button 
                onClick={loadAccounts}
                className="btn btn-sm btn-light"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              {/* All Accounts Option */}
              <button
                onClick={() => handleAccountSelect(null)}
                className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-coal-500 rounded-lg transition-colors"
              >
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <i className="ki-filled ki-profile-user text-primary"></i>
                </div>
                <div className="flex-1">
                  <div className="font-medium">All Accounts</div>
                  <div className="text-sm text-gray-500">View data from all accounts</div>
                </div>
              </button>

              {/* Account List */}
              {filteredAccounts.length === 0 && searchTerm ? (
                <div className="text-center py-8 text-gray-500">
                  No accounts found matching "{searchTerm}"
                </div>
              ) : (
                filteredAccounts.map((account) => (
                  <button
                    key={account.unique_identifier}
                    onClick={() => handleAccountSelect(account)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 dark:hover:bg-coal-500 rounded-lg transition-colors"
                  >
                    <div className="w-10 h-10 bg-gray-100 dark:bg-coal-500 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                        {account.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="font-medium">{account.name}</div>
                      <div className="text-sm text-gray-500">
                        {account.unique_identifier}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t dark:border-coal-400">
          <button
            onClick={onClose}
            className="btn btn-light w-full"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileAccountSwitcher;